import React, { useState, useEffect } from 'react';
import { useAuthStore } from '@/store/authStore';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Eye,
  Search,
  RefreshCw,
  Building2,
  Phone,
  Mail,
  MapPin,
  Globe,
  Clock,
  Star,
  X,
  Check,
  XCircle
} from 'lucide-react';

interface Business {
  id: string;
  business_name: string;
  description?: string;
  category: string;
  subcategory?: string;
  services?: string;
  contact_email?: string;
  contact_phone: string;
  website?: string;
  address?: string;
  block_number?: string;
  flat_number?: string;
  business_hours?: any;
  attachments?: any;
  is_verified: boolean;
  is_featured: boolean;
  is_active: boolean;
  rating?: number;
  review_count?: number;
  social_media?: any;
  created_at: string;
  updated_at?: string;
}

const BusinessPage = () => {
  const { user } = useAuthStore();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  const [editingBusiness, setEditingBusiness] = useState<Business | null>(null);
  const [formData, setFormData] = useState({
    business_name: '',
    description: '',
    category: '',
    subcategory: '',
    services: '',
    contact_email: '',
    contact_phone: '',
    website: '',
    address: '',
    block_number: '',
    flat_number: '',
    is_verified: false,
    is_featured: false,
    is_active: true
  });
  const [images, setImages] = useState<string[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterVerified, setFilterVerified] = useState<string>('all');

  const categories = [
    'Restaurant',
    'Retail',
    'Services',
    'Healthcare',
    'Education',
    'Automotive',
    'Real Estate',
    'Entertainment',
    'Other'
  ];

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/business?limit=100', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBusinesses(data.data || []);
      } else {
        setError('Failed to fetch businesses');
      }
    } catch (error) {
      console.error('Error fetching businesses:', error);
      setError('Failed to fetch businesses');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    setError(null);

    try {
      const { imageUploadService } = await import('@/services/imageUploadService');
      const fileArray = Array.from(files);
      
      const results = await imageUploadService.uploadMultipleImages(fileArray, 'general', true);
      
      const successfulUploads = results.filter(r => r.success);
      
      if (successfulUploads.length > 0) {
        const newUrls = successfulUploads.map(r => {
          if (r.url && (r.url.startsWith('http://') || r.url.startsWith('https://'))) {
            return r.url;
          }
          return imageUploadService.getPublicUrl(r.url);
        });
        setImages(prev => [...prev, ...newUrls]);
        setSuccess(`${successfulUploads.length} image(s) uploaded successfully`);
        setTimeout(() => setSuccess(null), 3000);
      }

      if (results.some(r => !r.success)) {
        setError('Some images failed to upload');
        setTimeout(() => setError(null), 3000);
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      setError('Failed to upload images');
      setTimeout(() => setError(null), 3000);
    } finally {
      setUploadingImages(false);
      e.target.value = '';
    }
  };

  const removeImage = (index: number) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const businessData = {
        ...formData,
        // Ensure is_active is always explicitly set (default to true for new businesses)
        is_active: editingBusiness ? formData.is_active : (formData.is_active !== undefined ? formData.is_active : true),
        attachments: images.length > 0 ? { images } : undefined
      };

      const url = editingBusiness 
        ? `/api/v1/business/${editingBusiness.id}`
        : '/api/v1/business';
      
      const method = editingBusiness ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(businessData)
      });

      if (response.ok) {
        setSuccess(editingBusiness ? 'Business updated successfully' : 'Business created successfully');
        setShowModal(false);
        resetForm();
        fetchBusinesses();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to save business');
        setTimeout(() => setError(null), 3000);
      }
    } catch (error) {
      console.error('Error saving business:', error);
      setError('Failed to save business');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleEdit = async (business: Business) => {
    setEditingBusiness(business);
    setFormData({
      business_name: business.business_name,
      description: business.description || '',
      category: business.category,
      subcategory: business.subcategory || '',
      services: business.services || '',
      contact_email: business.contact_email || '',
      contact_phone: business.contact_phone,
      website: business.website || '',
      address: business.address || '',
      block_number: business.block_number || '',
      flat_number: business.flat_number || '',
      is_verified: business.is_verified,
      is_featured: business.is_featured,
      is_active: business.is_active
    });

    if (business.attachments && business.attachments.images) {
      try {
        const { imageUploadService } = await import('@/services/imageUploadService');
        const imageArray = Array.isArray(business.attachments.images) ? business.attachments.images : [];
        const publicUrls = imageArray.map((url: string) => {
          if (!url) return '';
          return imageUploadService.getPublicUrl(url);
        });
        setImages(publicUrls.filter(url => url !== ''));
      } catch (error) {
        console.error('Error loading images:', error);
        setImages([]);
      }
    } else {
      setImages([]);
    }

    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this business?')) {
      return;
    }

    try {
      const response = await fetch(`/api/v1/business/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSuccess('Business deleted successfully');
        fetchBusinesses();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to delete business');
        setTimeout(() => setError(null), 3000);
      }
    } catch (error) {
      console.error('Error deleting business:', error);
      setError('Failed to delete business');
      setTimeout(() => setError(null), 3000);
    }
  };

  const resetForm = () => {
    setFormData({
      business_name: '',
      description: '',
      category: '',
      subcategory: '',
      services: '',
      contact_email: '',
      contact_phone: '',
      website: '',
      address: '',
      block_number: '',
      flat_number: '',
      is_verified: false,
      is_featured: false,
      is_active: true
    });
    setImages([]);
    setEditingBusiness(null);
  };

  const filteredBusinesses = businesses.filter(business => {
    const matchesSearch = !searchTerm || 
      business.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      business.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      business.category.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = filterCategory === 'all' || business.category === filterCategory;
    const matchesVerified = filterVerified === 'all' || 
      (filterVerified === 'verified' && business.is_verified) ||
      (filterVerified === 'unverified' && !business.is_verified);

    return matchesSearch && matchesCategory && matchesVerified;
  });

  // Check if user is admin - ensure user is loaded
  const isAdmin = user && (user.role === 'universal_admin' || user.role === 'office_admin');

  // Debug: Log user info to help troubleshoot
  useEffect(() => {
    if (user) {
      console.log('Business Page - User:', { id: user.id, role: user.role, isAdmin });
    } else {
      console.log('Business Page - No user logged in');
    }
  }, [user, isAdmin]);

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Business Directory</h1>
              <p className="mt-2 text-gray-600">Manage business listings for the community</p>
              {!isAdmin && user && (
                <p className="mt-1 text-sm text-amber-600">
                  ⚠️ Admin access required to add businesses. Your role: {user.role}
                </p>
              )}
            </div>
            {isAdmin && (
              <button
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
                className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Business
              </button>
            )}
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
            {success}
          </div>
        )}

        {/* Filters */}
        <div className="mb-6 bg-white rounded-lg shadow-sm p-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search businesses..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Verification</label>
              <select
                value={filterVerified}
                onChange={(e) => setFilterVerified(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All</option>
                <option value="verified">Verified</option>
                <option value="unverified">Unverified</option>
              </select>
            </div>
          </div>
        </div>

        {/* Business List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        ) : filteredBusinesses.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No businesses found</h3>
            <p className="text-gray-500">Try adjusting your filters or add a new business.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBusinesses.map((business) => (
              <div key={business.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden">
                {/* Business Image */}
                {business.attachments && business.attachments.images && business.attachments.images.length > 0 ? (
                  <div className="w-full h-48 overflow-hidden">
                    <img
                      src={business.attachments.images[0]}
                      alt={business.business_name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-full h-48 bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                    <Building2 className="w-16 h-16 text-blue-400" />
                  </div>
                )}

                <div className="p-4 sm:p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{business.business_name}</h3>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-medium px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                          {business.category}
                        </span>
                        {business.is_verified && (
                          <span className="text-xs font-medium px-2 py-1 bg-green-100 text-green-800 rounded-full flex items-center">
                            <Check className="w-3 h-3 mr-1" />
                            Verified
                          </span>
                        )}
                        {business.is_featured && (
                          <span className="text-xs font-medium px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full">
                            Featured
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {business.description && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{business.description}</p>
                  )}

                  <div className="space-y-2 mb-4">
                    {business.contact_phone && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Phone className="w-4 h-4 mr-2 text-gray-400" />
                        <span>{business.contact_phone}</span>
                      </div>
                    )}
                    {business.contact_email && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Mail className="w-4 h-4 mr-2 text-gray-400" />
                        <span className="truncate">{business.contact_email}</span>
                      </div>
                    )}
                    {business.address && (
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                        <span className="truncate">{business.address}</span>
                      </div>
                    )}
                  </div>

                  {business.rating !== undefined && business.rating > 0 && (
                    <div className="flex items-center mb-4">
                      <Star className="w-4 h-4 text-yellow-400 fill-current mr-1" />
                      <span className="text-sm font-medium text-gray-900">{business.rating.toFixed(1)}</span>
                      {business.review_count && (
                        <span className="text-sm text-gray-500 ml-1">({business.review_count} reviews)</span>
                      )}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setSelectedBusiness(business);
                        setShowDetailModal(true);
                      }}
                      className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                    >
                      <Eye className="w-4 h-4 inline mr-1" />
                      View
                    </button>
                    {isAdmin && (
                      <>
                        <button
                          onClick={() => handleEdit(business)}
                          className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(business.id)}
                          className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingBusiness ? 'Edit Business' : 'Add New Business'}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Business Name */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.business_name}
                      onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      required
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Category</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  {/* Subcategory */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subcategory
                    </label>
                    <input
                      type="text"
                      value={formData.subcategory}
                      onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Contact Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Phone *
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Contact Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Email
                    </label>
                    <input
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Website */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Website
                    </label>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://example.com"
                    />
                  </div>

                  {/* Address */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address
                    </label>
                    <textarea
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      rows={2}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Block & Flat Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Block Number
                    </label>
                    <input
                      type="text"
                      value={formData.block_number}
                      onChange={(e) => setFormData({ ...formData, block_number: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Flat Number
                    </label>
                    <input
                      type="text"
                      value={formData.flat_number}
                      onChange={(e) => setFormData({ ...formData, flat_number: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Description */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Services */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Services
                    </label>
                    <textarea
                      value={formData.services}
                      onChange={(e) => setFormData({ ...formData, services: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="List services offered (one per line)"
                    />
                  </div>

                  {/* Images */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Images
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      disabled={uploadingImages}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {uploadingImages && (
                      <p className="mt-2 text-sm text-gray-500">Uploading images...</p>
                    )}
                    {images.length > 0 && (
                      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {images.map((url, index) => (
                          <div key={index} className="relative">
                            <img
                              src={url}
                              alt={`Business ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Checkboxes */}
                  <div className="md:col-span-2 space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_verified}
                        onChange={(e) => setFormData({ ...formData, is_verified: e.target.checked })}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Verified Business</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_featured}
                        onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Featured Business</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Active</span>
                    </label>
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {editingBusiness ? 'Update Business' : 'Create Business'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {showDetailModal && selectedBusiness && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowDetailModal(false)}>
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Business Details</h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6">
                {/* Images */}
                {selectedBusiness.attachments && selectedBusiness.attachments.images && selectedBusiness.attachments.images.length > 0 && (
                  <div className="mb-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {selectedBusiness.attachments.images.map((url: string, index: number) => (
                        <img
                          key={index}
                          src={url}
                          alt={`${selectedBusiness.business_name} - Image ${index + 1}`}
                          className="w-full h-64 object-cover rounded-lg"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Business Info */}
                <div className="mb-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-3xl font-bold text-gray-900 mb-2">{selectedBusiness.business_name}</h3>
                      <div className="flex flex-wrap gap-2">
                        <span className="text-sm font-medium px-3 py-1 bg-blue-100 text-blue-800 rounded-full">
                          {selectedBusiness.category}
                        </span>
                        {selectedBusiness.is_verified && (
                          <span className="text-sm font-medium px-3 py-1 bg-green-100 text-green-800 rounded-full flex items-center">
                            <Check className="w-4 h-4 mr-1" />
                            Verified
                          </span>
                        )}
                        {selectedBusiness.is_featured && (
                          <span className="text-sm font-medium px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full">
                            Featured
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {selectedBusiness.description && (
                    <p className="text-base text-gray-700 mb-6 whitespace-pre-wrap">{selectedBusiness.description}</p>
                  )}
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {selectedBusiness.contact_phone && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <Phone className="w-4 h-4 mr-2" />
                        Phone
                      </h4>
                      <p className="text-base text-gray-900 font-medium">{selectedBusiness.contact_phone}</p>
                    </div>
                  )}
                  {selectedBusiness.contact_email && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <Mail className="w-4 h-4 mr-2" />
                        Email
                      </h4>
                      <p className="text-base text-gray-900 font-medium">{selectedBusiness.contact_email}</p>
                    </div>
                  )}
                  {selectedBusiness.website && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <Globe className="w-4 h-4 mr-2" />
                        Website
                      </h4>
                      <a
                        href={selectedBusiness.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-base text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {selectedBusiness.website}
                      </a>
                    </div>
                  )}
                  {selectedBusiness.address && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <MapPin className="w-4 h-4 mr-2" />
                        Address
                      </h4>
                      <p className="text-base text-gray-900 font-medium">{selectedBusiness.address}</p>
                      {(selectedBusiness.block_number || selectedBusiness.flat_number) && (
                        <p className="text-sm text-gray-600 mt-1">
                          Block: {selectedBusiness.block_number || 'N/A'}, Flat: {selectedBusiness.flat_number || 'N/A'}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Services */}
                {selectedBusiness.services && (
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Services</h4>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-base text-gray-700 whitespace-pre-wrap">{selectedBusiness.services}</p>
                    </div>
                  </div>
                )}

                {/* Rating */}
                {selectedBusiness.rating !== undefined && selectedBusiness.rating > 0 && (
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Rating</h4>
                    <div className="flex items-center">
                      <Star className="w-6 h-6 text-yellow-400 fill-current mr-2" />
                      <span className="text-2xl font-bold text-gray-900">{selectedBusiness.rating.toFixed(1)}</span>
                      {selectedBusiness.review_count && (
                        <span className="text-base text-gray-500 ml-2">({selectedBusiness.review_count} reviews)</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessPage;
